import qs from 'qs';
import { AccountType, UserType, type AccountResponse, type ProfileResponse, type ProfileFeaturesResponse } from '@shared/api';
import DashboardIcon from '@assets/icons/dashboard.svg';
import AccountIcon from '@assets/icons/account.svg';
import PaymentIcon from '@assets/icons/payment.svg';
import CardIcon from '@assets/icons/card.svg';
import UsersIcon from '@assets/icons/users.svg';
import ReportIcon from '@assets/icons/report.svg';
import StatementIcon from '@assets/icons/statement.svg';
import IntegrationIcon from '@assets/icons/integration.svg';
import ChatIcon from '@assets/icons/chat.svg';
import { PaletteUtils } from './paletteUtils';

export class RoutePaths {
  static accounts = {
    path: 'accounts',
    onNavigate: () => '/accounts',
  };
  static account = {
    path: 'accounts/:id',
    onNavigate: (id: number) => `/accounts/${id}`,
  };
  static accountTransaction = {
    path: 'accounts/transactions/:id',
    onNavigate: (id: string, query?: { back?: string }) => `/accounts/transactions/${id}${qs.stringify(query, { addQueryPrefix: true })}`,
  };

  static alerts = {
    path: 'alerts',
    onNavigate: (query?: { id?: string }) => `/alerts${qs.stringify(query, { addQueryPrefix: true })}`,
  };

  static callback = {
    path: 'callback',
    onNavigate: () => '/callback',
  };

  static cards = {
    path: 'cards',
    onNavigate: () => '/cards',
  };
  static card = {
    path: 'cards/:id',
    onNavigate: (id: number) => `/cards/${id}`,
  };

  static dashboard = {
    path: '',
    onNavigate: () => '/',
  };

  static error = {
    path: 'error',
    onNavigate: () => '/error',
  };

  static integrations = {
    path: 'integrations',
    onNavigate: () => '/integrations',
  };
  static integration = {
    path: 'integrations/:id',
    onNavigate: (id: string) => `/integrations/${id}`,
  };

  static messages = {
    path: 'message-centre',
    onNavigate: () => '/message-centre',
  };

  static payments = {
    path: 'payments',
    onNavigate: () => '/payments',
  };
  static paymentsInsignia = {
    path: 'payments/insignia',
    onNavigate: () => '/payments/insignia',
  };
  static paymentsOther = {
    path: 'payments/other',
    onNavigate: () => '/payments/other',
  };

  static paymentsCard = {
    path: 'payments/card',
    onNavigate: () => '/payments/card',
  };
  static paymentsRecipient = {
    path: 'payments/recipient/:id',
    onNavigate: (id: string) => `/payments/recipient/${id}`,
  };
  static paymentRecipients = {
    path: 'payments/recipients',
    onNavigate: () => '/payments/recipients',
  };
  static paymentNewRecipients = {
    path: 'payments/recipients/new',
    onNavigate: () => '/payments/recipients/new',
  };

  static profile = {
    path: 'profile',
    onNavigate: () => '/profile',
  };

  static reports = {
    path: 'reports',
    onNavigate: () => '/reports',
  };

  static signup = {
    path: 'signup',
    onNavigate: () => '/signup',
  };

  static success = {
    path: 'success',
    onNavigate: () => '/success',
  };

  static statements = {
    path: 'statements',
    onNavigate: () => '/statements',
  };

  static users = {
    path: 'user-management',
    onNavigate: (query?: { successDeleted?: boolean }) => `/user-management${qs.stringify(query, { addQueryPrefix: true })}`,
  };
  static user = {
    path: 'user-management/:id',
    onNavigate: (id: string) => `/user-management/${id}`,
  };
}

const MENU = [
  { name: 'sidebar.menu.dashboard', link: RoutePaths.dashboard.onNavigate(), Icon: <DashboardIcon /> },
  { name: 'sidebar.menu.accounts', link: RoutePaths.accounts.onNavigate(), Icon: <AccountIcon /> },
  { name: 'sidebar.menu.payments', link: RoutePaths.payments.onNavigate(), Icon: <PaymentIcon /> },
  { name: 'sidebar.menu.cards', link: RoutePaths.cards.onNavigate(), Icon: <CardIcon /> },
  { name: 'sidebar.menu.userManagement', link: RoutePaths.users.onNavigate(), Icon: <UsersIcon /> },
  { name: 'sidebar.menu.statements', link: RoutePaths.statements.onNavigate(), Icon: <StatementIcon /> },
  { name: 'sidebar.menu.reports', link: RoutePaths.reports.onNavigate(), Icon: <ReportIcon /> },
  { name: 'sidebar.menu.integrations', link: RoutePaths.integrations.onNavigate(), Icon: <IntegrationIcon /> },
  { name: 'sidebar.menu.messageCentre', link: RoutePaths.messages.onNavigate(), Icon: <ChatIcon /> },
];

export class RouteUtils {
  static getAllowedMenuItems(props: { profile: ProfileResponse | null; features: ProfileFeaturesResponse | null; accounts: AccountResponse[] }) {
    const isShowMenuItem = (link: string) => {
      switch (link) {
        case RoutePaths.messages.onNavigate():
          return !!props.features?.messages?.viewOrReply;
        case RoutePaths.statements.onNavigate():
          return !!(
            props.features?.statements?.viewOrDownloadAccountStatement ||
            props.features?.statements?.viewOrDownloadCardStatement ||
            props.features?.statements?.viewOrDownloadConsolidatedStatement
          );
        case RoutePaths.users.onNavigate():
          return !!props.features?.userManagement?.viewUsers;
        case RoutePaths.reports.onNavigate():
          return !!props.features?.reports?.viewReportsSection;
        case RoutePaths.accounts.onNavigate():
          return !!props.features?.accountFlows?.length;
        case RoutePaths.integrations.onNavigate():
          return props.profile?.userType === UserType.Admin && !PaletteUtils.isPersonalPalette;
        case RoutePaths.payments.onNavigate():
          return props.accounts?.some((account) => account.type === AccountType.Payment);
        case RoutePaths.cards.onNavigate():
          return !!props.features?.cardFlows?.length;
        default:
          return true;
      }
    };

    return MENU.filter((item) => isShowMenuItem(item.link));
  }
}
